import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RevenueReportAggregateEnum } from 'anchor-scout/types';
import { Page, Button, Spinner, TableList, useStateFromUrl, PageContentHeader } from 'seagull';
import { routes } from "../../../constants";
import TableFilters from "../components/TableFilters/TableFilters";
import { selectors } from "../../../store/state-selectors";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { calculateStartDate, calculateEndDate } from "../components/DateRangeWithPresets/DateRangeWithPresets";
import { DateRange } from "../components/types";
import { formatLongMonthUSDate, formatNumericUSDate } from "../../../utils";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { RevenueBreakdownRow } from "./RevenueBreakdownRow";
import { defaultAggregate, filterLabels, getDefaultSort, getHeaderItems, makeCsv, makeFiltersConfig, revenueReportColumnDefs, revenueReportHeaderColumnDefs } from "./RevenueBreakdown.utils";
import { FilterType } from "./types";
export function RevenueBreakdownReport(_ref) {
  var _selectedFilters$Filt2;
  let {
    now
  } = _ref;
  const businessName = useStore(selectors.user.businessName);
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const {
    downloadCsv
  } = useIoContext();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const {
    urlState: {
      dateRange,
      dateRangeOption
    },
    changeUrlState
  } = useStateFromUrl({
    dateRange: {
      defaultValue: [calculateStartDate(formatNumericUSDate(now), DateRange.Last12Months, now), calculateEndDate(formatNumericUSDate(now), DateRange.Last12Months, now)],
      multi: true
    },
    dateRangeOption: {
      defaultValue: DateRange.Last12Months
    }
  });
  const [selectedFilters, setSelectedFilters] = useState({
    [FilterType.DateRange]: {
      dateRange,
      dateRangeOption
    },
    [FilterType.Aggregate]: defaultAggregate,
    [FilterType.Breakdowns]: []
  });
  const selectedDateRange = selectedFilters[FilterType.DateRange];
  useEffect(() => {
    selectedDateRange && changeUrlState(selectedDateRange);
  }, [selectedDateRange]);
  const headerItems = useMemo(() => getHeaderItems(selectedFilters[FilterType.Breakdowns] || [], selectedFilters[FilterType.Aggregate] || defaultAggregate), [selectedFilters]);
  const sortIds = useMemo(() => headerItems.map(item => item.sortId), [headerItems]);
  const defaultSort = useMemo(() => getDefaultSort(sortIds), [sortIds]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const sortStringified = sort && JSON.stringify(sort);
  useEffect(() => {
    document.title = 'Reports';
  }, []);
  const filtersConfig = useMemo(() => makeFiltersConfig(availableServices, availableCustomers), [availableServices, availableCustomers]);
  useEffect(() => {
    api.reports.revenueReportServices().then(setAvailableServices);
    api.reports.revenueReportCustomers().then(setAvailableCustomers);
  }, []);
  const fetchRevenueReportData = () => {
    setIsRefreshing(true);
    api.reports.revenueReport({
      start: dateRange[0],
      end: dateRange[1],
      breakdowns: selectedFilters[FilterType.Breakdowns] || [],
      aggregate: selectedFilters[FilterType.Aggregate] || defaultAggregate,
      customers: selectedFilters[FilterType.CustomerName] || [],
      services: selectedFilters[FilterType.ServiceName] || [],
      sort: sort ? [sortIds[sort.colIndex], sort.sort] : undefined
    }).then(data => {
      setReportData(data);
      setIsRefreshing(false);
    });
  };
  useEffect(() => {
    fetchRevenueReportData();
  }, /* eslint-disable react-hooks-static-deps/exhaustive-deps */
  [selectedFilters[FilterType.CustomerName], selectedFilters[FilterType.ServiceName], selectedFilters[FilterType.Breakdowns], selectedFilters[FilterType.Aggregate], dateRange[0], dateRange[1], sortStringified]);
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "revenue-breakdown-report",
    header: "Revenue Breakdown Report",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      skin: "primary",
      "data-testid": "export-report-btn",
      onClick: () => {
        var _selectedFilters$Filt;
        return downloadCsv(makeCsv(revenueReportHeaderColumnDefs[((_selectedFilters$Filt = selectedFilters[FilterType.Breakdowns]) === null || _selectedFilters$Filt === void 0 ? void 0 : _selectedFilters$Filt.join(',')) || ''](selectedFilters[FilterType.Aggregate] || defaultAggregate).map(_ref2 => {
          let {
            value
          } = _ref2;
          return value;
        }), reportData), "".concat(businessName, "_revenue_report_").concat(formatLongMonthUSDate(new Date()), ".csv"));
      }
    }, "Export"))
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: revenueReportColumnDefs[((_selectedFilters$Filt2 = selectedFilters[FilterType.Breakdowns]) === null || _selectedFilters$Filt2 === void 0 ? void 0 : _selectedFilters$Filt2.join(',')) || ''](selectedFilters[FilterType.Aggregate] || defaultAggregate),
    numRows: reportData.length,
    sort: sort,
    onSortChange: changeSort
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(TableFilters, {
    testIdPrefix: "revenue-breakdown-report",
    filterLabels: filterLabels,
    filterConfig: filtersConfig,
    selectedFilters: selectedFilters,
    setSelectedFilters: setSelectedFilters,
    onRefresh: fetchRevenueReportData
  })), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "revenue-breakdown-report-table-header"
  })), isRefreshing ? /*#__PURE__*/React.createElement("div", {
    "data-testid": "agreements-table-loading"
  }, /*#__PURE__*/React.createElement(Spinner, {
    centeredInBox: true
  })) : reportData.length ? reportData.map((reportRow, index) => /*#__PURE__*/React.createElement(RevenueBreakdownRow, {
    key: "".concat(reportRow.formattedDay, "-").concat(reportRow.customer, "-").concat(reportRow.service),
    index: index,
    day: reportRow.formattedDay,
    customer: reportRow.customer,
    service: reportRow.service,
    revenue: reportRow.revenue,
    breakdowns: selectedFilters[FilterType.Breakdowns] || [],
    showPeriod: selectedFilters[FilterType.Aggregate] !== RevenueReportAggregateEnum.AllTime
  })) : null));
}